'use client';

import { GRAPHQL_API_PATH } from './config';
import { GraphQLClient } from 'graphql-request';
import type { I18n } from '@funkey/locales';
import { contextFactory } from '~/utils/react';
import { std, ts } from '@funkey/shared/utils';
import { useI18n } from '~/features/i18n';
import { useMemo } from 'react';
import useNotifications from '~/features/notifications/useNotifications';
import { CONFIG } from '~/env';
import type { GraphQL_API_Headers } from '~/app/api/graphql/context';

type GraphQLClientCtxConfig = Omit<
    NonNullable<ConstructorParameters<typeof GraphQLClient>[1]>,
    'credentials' | 'responseMiddleware' | 'headers'
> & {
    headers: Omit<ts.Object.require<GraphQL_API_Headers, 'x-portal'>, 'accept-language'>;
};

const [useGraphQLClient, GraphQLClientProvider] = contextFactory(
    (props: GraphQLClientCtxConfig) => {
        const i18n = useI18n();
        const notificationCtx = useNotifications();

        const client = useMemo(() => createClient(props, i18n, notificationCtx), [i18n]);

        return useMemo(
            () =>
                ({
                    request: client.request.bind(client),
                    rawRequest: client.rawRequest.bind(client),
                    config: { ...props, locale: i18n.locale },
                }) as const,
            [client, props],
        );
    },
);

export { useGraphQLClient, GraphQLClientProvider };

function createClient(
    props: GraphQLClientCtxConfig,
    i18n: I18n,
    notificationCtx: ReturnType<typeof useNotifications>,
) {
    const { headers, ...config } = props;

    const reqHeaders = new Headers({
        'Accept-Language': i18n.locale,
    });

    if (headers !== undefined) {
        std.Object.entries(headers).forEach(([key, value]) => {
            if (value !== undefined) reqHeaders.set(key, value);
        });
    }

    return new GraphQLClient(`${CONFIG.APP_URL}${GRAPHQL_API_PATH}`, {
        ...config,
        fetch,
        credentials: 'include',
        headers: reqHeaders,
        responseMiddleware: (responseOrError: any) => {
            if (!(responseOrError instanceof Error)) return;

            let { message } = responseOrError;

            if ('response' in responseOrError && responseOrError.response) {
                const response: any = responseOrError.response;

                if (response.errors?.length > 0) {
                    const [error] = response.errors;
                    message = error.message;

                    // Handle user input errors silently
                    if (error.extensions?.code === 'USER_INPUT_ERROR') {
                        return;
                    }
                }
                if (response.error) {
                    message = response.error;
                }
            }

            if (!message.includes('UNAVAILABLE')) {
                notificationCtx.dispatch({
                    type: 'ERROR',
                    title: <>{i18n._('Server Error')}</>,
                    message: message || i18n._('Something went wrong. Please try again later.'),
                });
            }
        },
    });
}
